/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type CounterData, PreOrderQuantitySelector } from '@/components/Cart/CommonCounterPart';
import { countryCodes } from '@/constants/locale';
import { useFeatureFlag } from '@/features/globalConfig';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useLocale } from '@/hooks/useLocale';
import type {
  AvailabilityInfoVOFromAPIV2,
  SkuPricingAndAvailabilityFromAPI,
} from '@/types/availability';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { getDealPrice } from './utils/getDealPrice';
import { getPopoverPrices } from './utils/getPopoverPrices';
import { type ShippingType } from '@/features/fulfillment';

type PriceQuantityProps = {
  configurableSku: boolean;
  discountedPrice: string | null;
  retailPrice: string[];
  availProduct?: SkuPricingAndAvailabilityFromAPI;
  staticPrice: number;
  handleQuantityChange: (
    quantity: number,
    quantityErrorMsg: boolean,
    showQuantityWarningMessage?: boolean,
  ) => void;
  availabilityCheck: AvailabilityInfoVOFromAPIV2;
  sisterStoreAvailable: boolean;
  skuId: string;
  eligibleForNextDay?: boolean;
  count: number;
  shippingType: ShippingType;
  pageType: string;
  customizeSKUs: boolean | undefined;
  fulfillmentId: number;
  setFulfillmentId: (fulfillmentId: number) => void;
  quantityInputDisabled?: boolean;
  productTitle: string;
  stepperButtonClassName?: string;
};

const CalmWrapper = (props: PriceQuantityProps) => {
  const {
    configurableSku,
    discountedPrice,
    retailPrice,
    availProduct,
    count,
    shippingType,
    staticPrice,
    handleQuantityChange,
    availabilityCheck,
    sisterStoreAvailable,
    skuId,
    eligibleForNextDay,
    pageType,
    customizeSKUs,
    fulfillmentId,
    setFulfillmentId,
    quantityInputDisabled,
    productTitle,
    stepperButtonClassName,
  } = props;
  const strikeThroughPricingEnabled = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const isDesktopOrTablet = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const locale = useLocale();
  const { data: storeData } = useStoreDetailsData();
  const isBopusEnabled = storeData?.bopusEnabled ?? false;
  const isBopusMexicoStoreEnabled = locale === countryCodes.mx && isBopusEnabled;
  const isMX = locale === countryCodes.mx;
  const isBR = locale === countryCodes.ptBr;

  const [retailPriceDollars, retailPriceCents] = retailPrice;

  let retailPriceInt = Number(retailPriceDollars) + Number(retailPriceCents) / 100;

  const dealPrice = getDealPrice(
    availProduct?.lowestPrice,
    strikeThroughPricingEnabled,
    discountedPrice,
    configurableSku,
    retailPrice,
  );

  const counterData: CounterData = {
    itemQuantity: count,
    orderType: shippingType,
    eligibleForNextDay: eligibleForNextDay,
    productInfo: {
      skuId: String(skuId),
      name: productTitle,
    },
    unitAmount: staticPrice,
    lineItemPriceInfo: {
      unitPrice: staticPrice,
      retailPrice: retailPriceInt,
    },
    fulfillmentId: fulfillmentId,
  };

  const { counterPrice } = getPopoverPrices({
    wasRetailPrice: availProduct?.wasRetailPrice ?? undefined,
    retailPriceInt,
    discountedPrice,
    count,
    staticPrice,
    dealPrice,
  });

  const ariaDescribedBy = 'pdp-part-number pdp-price';

  if (!customizeSKUs && ((isMX && isBopusMexicoStoreEnabled) || (!isMX && !isBR))) {
    return (
      <div data-testid={isDesktopOrTablet ? 'quantity-counter-lg' : 'quantity-counter-sm'}>
        <PreOrderQuantitySelector
          variant="calm"
          data={counterData}
          disabled={quantityInputDisabled}
          corePrice={availProduct?.corePrice}
          discountedStrikeThroughPrice={counterPrice}
          availabilityCheck={availabilityCheck}
          sisterStoreAvailable={sisterStoreAvailable}
          handleQuantityChange={handleQuantityChange}
          pageType={pageType}
          fulfillmentId={fulfillmentId}
          setFulfillmentId={setFulfillmentId}
          isNewMessagingShown={locale !== countryCodes.mx}
          isMini
          stepperButtonClassName={stepperButtonClassName}
          ariaDescribedBy={ariaDescribedBy}
        />
      </div>
    );
  }

  return null;
};

export default CalmWrapper;
